/**
 * CodeSnippet
 *
 * A component for displaying interactive code snippets in chat messages
 *
 * Primary responsibilities:
 * - Display formatted code with syntax highlighting
 * - Provide copy to clipboard functionality
 * - Allow saving snippet as a file
 * - Support running/executing code with mock console
 */

import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconCode,
  IconCopy,
  IconCheck,
  IconDownload,
  IconTerminal2,
  IconX,
  IconMaximize,
  IconMinimize
} from '@tabler/icons-react'

interface CodeSnippetProps {
  code: string
  language?: string
  title?: string
  description?: string
  className?: string
  canExecute?: boolean
}

export const CodeSnippet: React.FC<CodeSnippetProps> = ({
  code,
  language = 'javascript',
  title = 'Code Snippet',
  description,
  className,
  canExecute = false
}) => {
  const [copied, setCopied] = useState(false)
  const [showConsole, setShowConsole] = useState(false)
  const [consoleOutput, setConsoleOutput] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const consoleRef = useRef<HTMLDivElement>(null)

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const saveAsFile = () => {
    const filename =
      title.toLowerCase().replace(/\s+/g, '-') +
      '.' +
      (language === 'javascript'
        ? 'js'
        : language === 'typescript'
          ? 'ts'
          : language === 'html'
            ? 'html'
            : language === 'css'
              ? 'css'
              : 'txt')

    const blob = new Blob([code], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const executeCode = () => {
    if (!canExecute) return

    setIsLoading(true)
    setHasError(false)
    setConsoleOutput([])
    setShowConsole(true)

    // Capture console logs
    const originalConsoleLog = console.log
    const originalConsoleError = console.error
    const originalConsoleWarn = console.warn
    const originalConsoleInfo = console.info

    const logs: string[] = []

    console.log = (...args) => {
      logs.push(
        args
          .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
          .join(' ')
      )
      originalConsoleLog(...args)
    }

    console.error = (...args) => {
      logs.push(
        `Error: ${args
          .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
          .join(' ')}`
      )
      originalConsoleError(...args)
    }

    console.warn = (...args) => {
      logs.push(
        `Warning: ${args
          .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
          .join(' ')}`
      )
      originalConsoleWarn(...args)
    }

    console.info = (...args) => {
      logs.push(
        `Info: ${args
          .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
          .join(' ')}`
      )
      originalConsoleInfo(...args)
    }

    // Execute the code in a try-catch block
    setTimeout(() => {
      try {
        // Create a function from the code string
        // eslint-disable-next-line no-new-func
        const fn = new Function(code)
        fn()
        setConsoleOutput(logs)
      } catch (error) {
        setConsoleOutput([`Error: ${(error as Error).message}`])
        setHasError(true)
      } finally {
        // Restore original console methods
        console.log = originalConsoleLog
        console.error = originalConsoleError
        console.warn = originalConsoleWarn
        console.info = originalConsoleInfo

        setIsLoading(false)

        // Scroll to the bottom of the console
        if (consoleRef.current) {
          consoleRef.current.scrollTop = consoleRef.current.scrollHeight
        }
      }
    }, 100)
  }

  const toggleConsole = () => {
    if (!showConsole && canExecute) {
      executeCode()
    } else {
      setShowConsole(!showConsole)
    }
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        isExpanded ? 'fixed inset-4 z-50 max-h-[calc(100vh-2rem)]' : 'relative',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconCode size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
          {language && (
            <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              {language}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={copyToClipboard}
            className="size-8 p-0"
            title="Copy to clipboard"
          >
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={saveAsFile}
            className="size-8 p-0"
            title="Save as file"
          >
            <IconDownload size={16} />
          </Button>
          {canExecute && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleConsole}
              className="size-8 p-0"
              title="Run code"
            >
              <IconTerminal2 size={16} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
            className="size-8 p-0"
            title={isExpanded ? 'Minimize' : 'Maximize'}
          >
            {isExpanded ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
          </Button>
        </div>
      </div>

      {description && (
        <div className="text-muted-foreground border-b px-4 py-2 text-sm">{description}</div>
      )}

      <div className={cn('relative flex flex-col', isExpanded ? 'grow' : '')}>
        <pre
          className={cn(
            'bg-muted/40 overflow-auto p-4 text-sm',
            isExpanded ? 'grow' : 'max-h-[300px]'
          )}
        >
          <code>{code}</code>
        </pre>

        {showConsole && (
          <div className="border-t">
            <div className="bg-muted flex items-center justify-between p-2">
              <span className="text-xs font-medium">Console Output</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConsole(false)}
                className="size-6 p-0"
              >
                <IconX size={14} />
              </Button>
            </div>
            <div
              ref={consoleRef}
              className={cn(
                'overflow-auto bg-gray-900 p-3 font-mono text-sm text-gray-100 dark:bg-gray-950',
                isExpanded ? 'max-h-[40vh]' : 'max-h-[150px]'
              )}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="size-2 animate-pulse rounded-full bg-blue-400" />
                  <div className="size-2 animate-pulse rounded-full bg-blue-400" delay-150 />
                  <div className="size-2 animate-pulse rounded-full bg-blue-400" delay-300 />
                  <span className="text-xs text-gray-400">Executing code...</span>
                </div>
              ) : consoleOutput.length > 0 ? (
                <div className="space-y-1">
                  {consoleOutput.map((line, index) => (
                    <div
                      key={index}
                      className={cn(
                        'break-words font-mono text-xs',
                        line.startsWith('Error:')
                          ? 'text-red-400'
                          : line.startsWith('Warning:')
                            ? 'text-yellow-400'
                            : line.startsWith('Info:')
                              ? 'text-blue-400'
                              : 'text-gray-200'
                      )}
                    >
                      {line}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-400">No output</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
